import { Container } from '@/components/ui/container';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator, ClipboardCheck } from 'lucide-react';
import Link from 'next/link';
import { LucideIcon } from 'lucide-react';

type FeaturedTool = {
	name: string;
	description: string;
	href: string;
	cta: string;
	icon: LucideIcon;
	accent: string;
};

const featuredTools: FeaturedTool[] = [
	{
		name: 'Automation ROI Calculator',
		description:
			'See exactly what manual, repetitive work is costing you every year — and what automating it could put back on your bottom line.',
		href: '/tools/automation-roi-calculator',
		cta: 'Calculate my ROI',
		icon: Calculator,
		accent: 'text-orange-500 bg-orange-500/10',
	},
	{
		name: 'Tech Audit Self-Assessment',
		description:
			'16 quick questions across data, automation, reporting, and security. Get a scored readout of where your systems stand today.',
		href: '/tools/tech-audit-assessment',
		cta: 'Take the assessment',
		icon: ClipboardCheck,
		accent: 'text-purple-400 bg-purple-500/10',
	},
];

export default function LandingTools(): JSX.Element {
	return (
		<Container>
			<div className="text-center mb-12 max-w-2xl mx-auto">
				<h2 className="text-3xl md:text-4xl font-bold mb-4 font-space-grotesk">
					Start With a Free Diagnostic
				</h2>
				<p className="text-muted-foreground text-lg">
					Before we talk next steps, see where your business actually stands. These are the same
					tools I use with fractional CTO clients — free, instant, no strings attached.
				</p>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
				{featuredTools.map((tool, index) => (
					<motion.div
						key={tool.name}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
					>
						<Link
							href={tool.href}
							className="space-card group flex h-full flex-col rounded-xl p-6 sm:p-8 transition-all duration-300 hover:border-orange-500/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.08)]"
						>
							<div
								className={`mb-5 flex h-12 w-12 items-center justify-center rounded-full ${tool.accent}`}
							>
								<tool.icon className="h-6 w-6" />
							</div>
							<h3 className="mb-3 text-xl sm:text-2xl font-bold font-space-grotesk">
								{tool.name}
							</h3>
							<p className="mb-6 flex-grow text-muted-foreground">{tool.description}</p>
							<span className="inline-flex items-center gap-2 font-semibold text-orange-500 group-hover:text-orange-400">
								{tool.cta}
								<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
							</span>
						</Link>
					</motion.div>
				))}
			</div>
		</Container>
	);
}
